import { ReactNode, useEffect, useState } from 'react';
import { InternalAxiosRequestConfig, AxiosResponse } from 'axios';

type ApiAxiosResponse = AxiosResponse & { error?: object };
import { convertKeysToCamelCase, convertKeysToSnakeCase } from 'utils/helpers';
import { useAuth } from 'context/AuthContext';
import { API } from 'api';

const AxiosInterceptor = ({ children }: { children: ReactNode }) => {
  const { logout } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const requestInterceptor = API.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const newConfig = { ...config };
      newConfig.url = config.url ?? '';
      if (newConfig.headers['Content-Type'] === 'multipart/form-data') return newConfig;
      if (config.params) {
        newConfig.params = convertKeysToSnakeCase(config.params as object);
      }
      if (config.data) {
        newConfig.data = convertKeysToSnakeCase(config.data as object);
      }
      return newConfig;
    });

    const responseInterceptor = API.interceptors.response.use(
      (response: ApiAxiosResponse) => {
        if (response.error) {
          response.error = convertKeysToCamelCase(response.error);
        }
        if (response.data) {
          response.data = convertKeysToCamelCase(response.data as object);
        }
        return response;
      },
      async (error) => {
        if (error.response.status === 401 || error.response.status === 403) {
          return logout();
        }
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        return Promise.reject(convertKeysToCamelCase(error.response.data));
      },
    );

    setIsInitialized(true);

    return () => {
      API.interceptors.request.eject(requestInterceptor);
      API.interceptors.response.eject(responseInterceptor);
      setIsInitialized(false);
    };
  }, [logout]);

  return isInitialized ? <>{children}</> : null;
};

export default AxiosInterceptor;
